import {
  Code as CodeIcon,
  Terminal as TerminalIcon,
} from 'lucide-react'
import { CodeSnippetExecState } from '@devbookhq/sdk'
import {
  CodeSnippetExtendedState,
  CodeSnippetState,
  useSharedSession,
} from '@devbookhq/react'
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Terminal,
  TerminalHandler,
} from '@devbookhq/terminal'
import cn from 'classnames'

import { Example } from 'utils/examplesContent'
import { TOCPage } from 'components/TOCSidebar/TOCPage'
import Code, {
  Handler as CodeHandler,
  Props as CodeProps,
} from 'components/Code'
import Select, { SelectItem } from 'components/Select'
import SessionAsleepScreen from 'components/SessionAsleepScreen'
import Text from 'components/typography/Text'

import { Handler } from './Handler'
import { analytics } from 'utils/analytics'
import { rootdir } from 'utils/constants'
import ExampleExecutionScreen from 'components/ExampleExecutingScreen'
import useTerminalOutputAnalytics from 'utils/analytics/useTerminalOutputAnalytics'

export enum Tab {
  Description = 'Description',
  Code = 'Code',
  Terminal = 'Terminal',
}

const mobileTabs: SelectItem[] = [
  {
    text: 'Code',
    value: Tab.Code,
    icon: (
      <CodeIcon
        color="currentColor"
        size={12}
      />
    ),
  },
  {
    text: 'Terminal',
    value: Tab.Terminal,
    icon: (
      <TerminalIcon
        color="currentColor"
        size={12}
      />
    ),
  },
]

export interface Props {
  example: Example
  category: TOCPage
  subcategory: TOCPage
  onCodeChange: CodeProps['onCodeChange']
  onWakeUpSessionClick: (e: any) => void
  onRunClick: () => void
  onStopClick: () => void
}

function MobileContent({
  example,
  category,
  onRunClick,
  onStopClick,
  onCodeChange,
  onWakeUpSessionClick,
}: Props, ref: ForwardedRef<Handler>) {
  const terminalRef = useRef<TerminalHandler>(null)
  const codeEditorRef = useRef<CodeHandler>(null)
  const [activeMobileTab, setActiveMobileTab] = useState<Tab>(Tab.Code)
  const {
    state: sessionState,
    session,
    refresh,
  } = useSharedSession()
  const [cmdState, setCmdState] = useState<CodeSnippetExecState>(CodeSnippetExecState.Stopped)

  const executionState = useMemo<CodeSnippetState>(() => {
    switch (sessionState) {
    case 'closed':
      return CodeSnippetExecState.Stopped
    case 'opening':
      return CodeSnippetExtendedState.Loading
    case 'open':
      return cmdState
    }
  },
  [cmdState, sessionState])

  const handleTerminalUserLine = useCallback((line: string) => {
    const [, cmd] = line.split('$')
    if (!cmd) return
    const trimmedCmd = cmd.trim()
    if (trimmedCmd.length > 0) {
      analytics.track('terminal line written', { cmd: trimmedCmd })
    }
  }, [])

  const handleTerminalCopy = useCallback((selection: string) => {
    if (selection.length > 0) {
      analytics.track('terminal selection copied', { selection })
    }
  }, [])

  function handleTabChange(tab: Tab) {
    setActiveMobileTab(tab)
  }

  function handleMobileRunClick() {
    if (activeMobileTab !== Tab.Terminal) {
      setActiveMobileTab(Tab.Terminal)
    }
    onRunClick()
  }

  const { handleOutput } = useTerminalOutputAnalytics()

  useImperativeHandle(ref,
    () => ({
      codeEditor() {
        return codeEditorRef.current
      },
      terminal() {
        return terminalRef.current
      },
    }))

  return (
    <div className={cn(
      'w-full',
      'fixed',
      'top-[2.8rem]',
      // 'bottom-[2.3rem]',
      'bottom-0',
      'flex',
      'flex-col',
    )}>
      <div className="
        px-2
        pb-2
        lg:py-2
        flex
        items-center
        justify-between
      ">
        <div className="
          flex
          items-center
          space-x-1
        ">
          <Text
            text="Active view"
          />
          <Select
            defaultItem={mobileTabs.find(t => t.value === Tab.Code)!}
            groups={[
              {
                label: 'Views',
                items: mobileTabs,
              },
            ]}
            selectedValue={activeMobileTab}
            onValueChange={v => handleTabChange(v as Tab)}
          />
        </div>
      </div>

      {
        activeMobileTab !== Tab.Description &&
        activeMobileTab !== Tab.Code &&
        (
          <>
            <SessionAsleepScreen
              isHidden={sessionState !== 'closed'}
              onWakeUpClick={onWakeUpSessionClick}
            />
            <ExampleExecutionScreen
              state={cmdState}
              stop={onStopClick}
            />
          </>
        )
      }

      <Code
        csState={executionState}
        docsLink={example.docsLink}
        docsLinkTitle={example.docsLinkTitle}
        exampleTitle={example.title}
        isHidden={activeMobileTab !== Tab.Code}
        ref={codeEditorRef}
        tabs={example.tabs}
        onCodeChange={onCodeChange}
        onRunClick={handleMobileRunClick}
        onStopClick={onStopClick}
      />

      <Terminal
        isHidden={activeMobileTab !== Tab.Terminal}
        ref={terminalRef}
        rootdir={rootdir}
        session={session}
        canStartTerminalSession
        isReadOnly
        onCopy={handleTerminalCopy}
        onLine={handleTerminalUserLine}
        onOutput={handleOutput}
        onRunningCmdChange={setCmdState}
      />
    </div>
  )
}

const ExampleMobileContent = forwardRef<Handler, Props>(MobileContent)

ExampleMobileContent.displayName = 'ExampleMobileContent'

export default ExampleMobileContent
